import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { TabBarContext } from "@/components/nav/TabBarContext";
import FeedbackModal from "@/components/feedback/FeedbackModal";
import {
  disarmFeedbackPrompt, isArmed, subscribeFeedbackPrompt,
} from "@/lib/feedbackPrompt";

/**
 * Owns the survey modal, mounted once at the root (T15-M3).
 *
 * Two ways in, and they differ in exactly one respect:
 *
 *   * the AUTOMATIC prompt, armed by the three trigger moments, offered once
 *     per account and gated on `profile.feedback_prompt_done`;
 *   * the Menú entry, which calls `openFeedback()` on the window and is
 *     repeatable forever.
 *
 * The entry point is a window event rather than a React context because the
 * two callers are in different branches of the tree — the Menú sheet lives
 * inside `MobileTabBar`, the sidebar row inside `DashboardLayout` — and a
 * context spanning both would have to wrap the router. The same reasoning
 * `Chat.jsx` used for `fixy:notifications-changed`: one listener, no provider.
 *
 * **Two guards, and neither should ever fire.** Every trigger site is a page
 * with no other modal open and none of them is the Dany thread, so by
 * construction the prompt cannot land on top of anything. They are here because
 * "by construction" is a claim about today's three call sites, and the next one
 * will be added by somebody reading the call site and not this file.
 */
export default function FeedbackHost() {
  const { user } = useAuth();
  const ctx = useContext(TabBarContext);
  // The bar hides exactly when a screen IS the viewport: the full-screen chat
  // thread, the Dany thread, and a voice call inside it. That is a better
  // signal than a route list, because it is the same state the screens
  // themselves declare — and it is precisely "do not interrupt this".
  const screenIsOwned = !!ctx?.hidden;
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("prompt");

  const promptDone = !!user?.profile?.feedback_prompt_done;

  /** Another modal is painted right now. Every modal in this app is a
   *  hand-rolled fixed overlay rather than a library primitive, so there is no
   *  single attribute to read; `role="dialog"` is what they have in common and
   *  what a new one should carry. */
  const anotherModalOpen = useCallback(() => {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    return Array.from(dialogs).some(
      (el) => el.getAttribute("data-testid") !== "feedback-modal"
              && el.getClientRects().length > 0
    );
  }, []);

  useEffect(() => {
    if (open) return undefined;

    const tryOpen = () => {
      if (!isArmed()) return false;
      // No session, or the question has already been answered once.
      if (!user || promptDone) {
        disarmFeedbackPrompt();
        return false;
      }
      if (screenIsOwned || anotherModalOpen()) return false;
      disarmFeedbackPrompt();
      setSource("prompt");
      setOpen(true);
      return true;
    };

    if (tryOpen()) return undefined;
    const unsubscribe = subscribeFeedbackPrompt(tryOpen);
    // While armed and blocked, keep asking. A deferred prompt is one waiting
    // for a modal to close or a call to end, and both are events this component
    // cannot see; the poll ends the moment it succeeds or the arming is spent.
    const timer = setInterval(() => { if (!isArmed()) return; tryOpen(); }, 500);
    return () => { unsubscribe(); clearInterval(timer); };
  }, [open, user, promptDone, screenIsOwned, anotherModalOpen]);

  // The Menú door. Always open, never gated, never closes the gate.
  useEffect(() => {
    const onOpen = () => {
      if (!user) return;
      setSource("menu");
      setOpen(true);
    };
    window.addEventListener("fixy:open-feedback", onOpen);
    return () => window.removeEventListener("fixy:open-feedback", onOpen);
  }, [user]);

  return (
    <FeedbackModal open={open} source={source} onClose={() => setOpen(false)} />
  );
}
